import { useEffect, useRef, useState } from 'react';
import { formatExpiresCountdown } from '@entities/invoice-pay';

interface UseExpiresCountdownOptions {
  expiresAt: string | null | undefined;
  serverTime?: string | null;
  onExpire?: () => void;
}

export function useExpiresCountdown({
  expiresAt,
  serverTime,
  onExpire,
}: UseExpiresCountdownOptions) {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const expiredNotified = useRef(false);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  const offsetMs = useRef(0);

  useEffect(() => {
    if (!serverTime) {
      offsetMs.current = 0;
      return;
    }
    const server = Date.parse(serverTime);
    if (Number.isFinite(server)) {
      offsetMs.current = server - Date.now();
    }
  }, [serverTime]);

  useEffect(() => {
    expiredNotified.current = false;

    if (!expiresAt) {
      setSecondsLeft(null);
      return undefined;
    }

    const expiresMs = Date.parse(expiresAt);
    if (!Number.isFinite(expiresMs)) {
      setSecondsLeft(null);
      return undefined;
    }

    const tick = () => {
      const now = Date.now() + offsetMs.current;
      const left = Math.max(0, Math.floor((expiresMs - now) / 1000));
      setSecondsLeft(left);
      if (left <= 0 && !expiredNotified.current) {
        expiredNotified.current = true;
        onExpireRef.current?.();
      }
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);

  const formatted = secondsLeft == null ? null : formatExpiresCountdown(secondsLeft);
  const isUrgent = secondsLeft != null && secondsLeft > 0 && secondsLeft < 5 * 60;

  return { secondsLeft, formatted, isUrgent };
}
